import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {COLUMN_TYPE} from 'bulk-import/SpreadsheetBulkImportConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {
  AutomationOutputFileColumnType,
  AutomationOutputFileDestinationInfo,
  ClientAutomationOutputFileConfigSpec,
  isTransfer,
  OutputFileAction,
  REGISTRATION_ACTION_TYPES,
  RESULTS_ACTION_TYPES,
} from 'lab-automation/settings/RunSchemaSpecConstants';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';

export const VOLUME_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT,
  AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT,
  AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT,
]);
export const CONCENTRATION_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.FINAL_CONCENTRATION_VALUE,
  AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE,
]);

export const AMOUNT_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  ...VOLUME_COLUMN_TYPES,
  ...CONCENTRATION_COLUMN_TYPES,
]);
export const SOURCE_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.INPUT_CONTAINER_BARCODE,
  AutomationOutputFileColumnType.INPUT_PLATE_BARCODE,
  AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES,
  AutomationOutputFileColumnType.INPUT_BOX_BARCODE,
  AutomationOutputFileColumnType.INPUT_BOX_COORDINATES,
]);

const BOX_DESTINATION_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.OUTPUT_BOX_BARCODE,
  AutomationOutputFileColumnType.OUTPUT_BOX_COORDINATES,
]);
const CONTAINER_DESTINATION_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.OUTPUT_CONTAINER_BARCODE,
]);
const PLATE_DESTINATION_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE,
  AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES,
]);
export const DESTINATION_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  ...BOX_DESTINATION_COLUMN_TYPES,
  ...PLATE_DESTINATION_COLUMN_TYPES,
  ...CONTAINER_DESTINATION_COLUMN_TYPES,
]);

const ENTITY_COLUMN_TYPES_FOR_PURE_RESULTS = new Set([
  AutomationOutputFileColumnType.ENTITY_API_ID,
  AutomationOutputFileColumnType.ENTITY_NAME,
]);
const ENTITY_COLUMN_TYPES_FOR_PURE_TRANSFER = new Set([AutomationOutputFileColumnType.INPUT_ENTITY_NAME]);
const ENTITY_COLUMN_TYPES_FOR_PLATE_TRANSFER = new Set([AutomationOutputFileColumnType.ENTITY_API_ID]);
const ENTITY_COLUMN_TYPES_FOR_REGISTRATION = new Set([
  AutomationOutputFileColumnType.NEW_ENTITY_NAME,
  AutomationOutputFileColumnType.ALIAS,
]);
export const ENTITY_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  ...ENTITY_COLUMN_TYPES_FOR_PURE_RESULTS,
  ...ENTITY_COLUMN_TYPES_FOR_PURE_TRANSFER,
  ...ENTITY_COLUMN_TYPES_FOR_REGISTRATION,
]);

export const SCHEMA_FIELD_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.SCHEMA_FIELD,
]);
export const RESIDUES_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.RESIDUES,
  AutomationOutputFileColumnType.HELM,
  AutomationOutputFileColumnType.CUSTOM_NOTATION,
]);
export const CHEMICAL_STRUCTURE_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.SMILES,
  AutomationOutputFileColumnType.MOLFILE,
]);
export const DATASET_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.DATASET,
]);
export const IGNORE_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.IGNORE,
]);
export const PROCEDURE_CONDITION_REPLICATES_COLUMN_TYPES: Set<AutomationOutputFileColumnType> = new Set([
  AutomationOutputFileColumnType.PROCEDURE_CONDITION_REPLICATES,
]);

/* Converts from Lab Automation to Bulk Import column type enums, must be kept in sync with the backend
 * version at benchling/lab_automation/process_automation_output_file.py
 */
function convertToBulkImportColumnType(columnType: AutomationOutputFileColumnType): COLUMN_TYPE {
  return AOFC_TO_COLUMN_TYPE[columnType];
}
const AOFC_TO_COLUMN_TYPE: {[aofColType in AutomationOutputFileColumnType]: COLUMN_TYPE} = {
  [AutomationOutputFileColumnType.ALIAS]: COLUMN_TYPE.ALIAS,
  [AutomationOutputFileColumnType.CUSTOM_NOTATION]: COLUMN_TYPE.CUSTOM_MOD_SYNTAX,
  [AutomationOutputFileColumnType.DATASET]: COLUMN_TYPE.DATASET,
  // TODO(BNCH-37783) include real mappings for this types once it is added to COLUMN_TYPES
  [AutomationOutputFileColumnType.ENTITY_API_ID]: COLUMN_TYPE.IGNORE,
  [AutomationOutputFileColumnType.ENTITY_NAME]: COLUMN_TYPE.FILENAME,
  [AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT]: COLUMN_TYPE.FINAL_QUANTITY_VALUE,
  [AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT]: COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE,
  [AutomationOutputFileColumnType.HELM]: COLUMN_TYPE.HELM,
  [AutomationOutputFileColumnType.IGNORE]: COLUMN_TYPE.IGNORE,
  [AutomationOutputFileColumnType.INPUT_BOX_BARCODE]: COLUMN_TYPE.INPUT_GRID_BARCODE,
  [AutomationOutputFileColumnType.INPUT_BOX_COORDINATES]: COLUMN_TYPE.INPUT_GRID_COORDINATES,
  [AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE]: COLUMN_TYPE.INPUT_CONCENTRATION_VALUE,
  // TODO(BNCH-37783) include real mappings for this types once it is added to COLUMN_TYPES
  [AutomationOutputFileColumnType.FINAL_CONCENTRATION_VALUE]: COLUMN_TYPE.IGNORE,
  [AutomationOutputFileColumnType.INPUT_CONTAINER_BARCODE]: COLUMN_TYPE.INPUT_CONTAINER_BARCODE,
  [AutomationOutputFileColumnType.INPUT_ENTITY_NAME]: COLUMN_TYPE.INPUT_BIOENTITY_LABEL,
  [AutomationOutputFileColumnType.INPUT_PLATE_BARCODE]: COLUMN_TYPE.INPUT_GRID_BARCODE,
  [AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES]: COLUMN_TYPE.INPUT_GRID_COORDINATES,
  [AutomationOutputFileColumnType.MOLFILE]: COLUMN_TYPE.MOLFILE,
  [AutomationOutputFileColumnType.NEW_ENTITY_NAME]: COLUMN_TYPE.NEW_BIOENTITY_NAME,
  [AutomationOutputFileColumnType.OUTPUT_BOX_BARCODE]: COLUMN_TYPE.OUTPUT_GRID_BARCODE,
  [AutomationOutputFileColumnType.OUTPUT_BOX_COORDINATES]: COLUMN_TYPE.OUTPUT_GRID_COORDINATES,
  [AutomationOutputFileColumnType.OUTPUT_CONTAINER_BARCODE]: COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE,
  [AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE]: COLUMN_TYPE.OUTPUT_GRID_BARCODE,
  [AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES]: COLUMN_TYPE.OUTPUT_GRID_COORDINATES,
  [AutomationOutputFileColumnType.PROCEDURE_CONDITION_REPLICATES]: COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES,
  [AutomationOutputFileColumnType.RESIDUES]: COLUMN_TYPE.RESIDUES,
  [AutomationOutputFileColumnType.SMILES]: COLUMN_TYPE.SMILES,
  [AutomationOutputFileColumnType.SCHEMA_FIELD]: COLUMN_TYPE.SCHEMA_FIELD,
  [AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT]: COLUMN_TYPE.TRANSFER_QUANTITY,
};

// In client code only, there are more granular *_SCHEMA_FIELD column types on the bulk import side
const SCHEMA_FIELD_COLUMN_TYPES_FOR_OUTPUT_FILE_CONFIG = [
  COLUMN_TYPE.SCHEMA_FIELD,
  COLUMN_TYPE.PLATE_SCHEMA_FIELD,
  COLUMN_TYPE.BOX_SCHEMA_FIELD,
  COLUMN_TYPE.CONTAINER_SCHEMA_FIELD,
  COLUMN_TYPE.RESULT_SCHEMA_FIELD,
];

export function convertToAutomationColumnType(columnType: COLUMN_TYPE): AutomationOutputFileColumnType {
  switch (columnType) {
    case COLUMN_TYPE.ALIAS:
      return AutomationOutputFileColumnType.ALIAS;
    case COLUMN_TYPE.CUSTOM_MOD_SYNTAX:
      return AutomationOutputFileColumnType.CUSTOM_NOTATION;
    case COLUMN_TYPE.FILENAME:
      return AutomationOutputFileColumnType.ENTITY_NAME;
    case COLUMN_TYPE.FINAL_QUANTITY_VALUE:
      return AutomationOutputFileColumnType.FINAL_QUANTITY_AMOUNT;
    case COLUMN_TYPE.SOURCE_FINAL_QUANTITY_VALUE:
      return AutomationOutputFileColumnType.SOURCE_FINAL_QUANTITY_AMOUNT;
    case COLUMN_TYPE.HELM:
      return AutomationOutputFileColumnType.HELM;
    case COLUMN_TYPE.IGNORE:
      return AutomationOutputFileColumnType.IGNORE;
    case COLUMN_TYPE.INPUT_BIOENTITY_LABEL:
      return AutomationOutputFileColumnType.INPUT_ENTITY_NAME;
    case COLUMN_TYPE.INPUT_CONCENTRATION_VALUE:
      return AutomationOutputFileColumnType.INPUT_CONCENTRATION_VALUE;
    case COLUMN_TYPE.INPUT_CONTAINER_BARCODE:
      return AutomationOutputFileColumnType.INPUT_CONTAINER_BARCODE;
    case COLUMN_TYPE.INPUT_GRID_BARCODE:
      return AutomationOutputFileColumnType.INPUT_PLATE_BARCODE;
    case COLUMN_TYPE.INPUT_GRID_COORDINATES:
      return AutomationOutputFileColumnType.INPUT_PLATE_COORDINATES;
    case COLUMN_TYPE.MOLFILE:
      return AutomationOutputFileColumnType.MOLFILE;
    case COLUMN_TYPE.NEW_BIOENTITY_NAME:
      return AutomationOutputFileColumnType.NEW_ENTITY_NAME;
    case COLUMN_TYPE.OUTPUT_CONTAINER_BARCODE:
      return AutomationOutputFileColumnType.OUTPUT_CONTAINER_BARCODE;
    case COLUMN_TYPE.OUTPUT_GRID_BARCODE:
      return AutomationOutputFileColumnType.OUTPUT_PLATE_BARCODE;
    case COLUMN_TYPE.OUTPUT_GRID_COORDINATES:
      return AutomationOutputFileColumnType.OUTPUT_PLATE_COORDINATES;
    case COLUMN_TYPE.PROCEDURE_CONDITION_REPLICATES:
      return AutomationOutputFileColumnType.PROCEDURE_CONDITION_REPLICATES;
    case COLUMN_TYPE.RESIDUES:
      return AutomationOutputFileColumnType.RESIDUES;
    case COLUMN_TYPE.SMILES:
      return AutomationOutputFileColumnType.SMILES;
    case COLUMN_TYPE.TRANSFER_QUANTITY:
      return AutomationOutputFileColumnType.TRANSFER_QUANTITY_AMOUNT;
    // TODO(BNCH-37783) include inverse mappings for ENTITY_API_ID and FINAL_CONCENTRATION_VALUE
    default: {
      const isSchemaColumn = assertAndContinue(
        SCHEMA_FIELD_COLUMN_TYPES_FOR_OUTPUT_FILE_CONFIG.includes(columnType),
        `Invalid COLUMN_TYPE ${columnType} with no AutomationOutputFileColumnType equivalent`
      );
      return isSchemaColumn
        ? AutomationOutputFileColumnType.SCHEMA_FIELD
        : AutomationOutputFileColumnType.IGNORE;
    }
  }
}

export function getAllValidColumnTypes(
  outputFileConfig: ClientAutomationOutputFileConfigSpec,
  allTagSchemasById: SchemaById
): Array<AutomationOutputFileColumnType> {
  const entitySchemaId = outputFileConfig.entitySchemaInfo?.schema;
  const entitySchema = entitySchemaId ? (allTagSchemasById[entitySchemaId] as TagSchema) : null;
  return Object.values(AutomationOutputFileColumnType).filter((columnType) =>
    includeColumnTypeOption(
      columnType,
      outputFileConfig.actionType,
      outputFileConfig.destinationInfo,
      entitySchema,
      outputFileConfig.shouldCreateDataset,
      outputFileConfig.shouldPerformPlateTransfer
    )
  );
}

export function getAllValidBulkImportColumnTypes(
  outputFileConfig: ClientAutomationOutputFileConfigSpec,
  allTagSchemasById: SchemaById
): Set<COLUMN_TYPE> {
  return new Set(
    getAllValidColumnTypes(outputFileConfig, allTagSchemasById).flatMap((labAutoColumnType) => {
      const bulkImportColumn = convertToBulkImportColumnType(labAutoColumnType);
      // SCHEMA_FIELD type maps to multiple bulk import columns and is a special case
      return bulkImportColumn === COLUMN_TYPE.SCHEMA_FIELD
        ? SCHEMA_FIELD_COLUMN_TYPES_FOR_OUTPUT_FILE_CONFIG
        : [bulkImportColumn];
    })
  );
}

function isFolderItemTypeWithResidues(folderItemType: string): boolean {
  return (
    [
      ModelTypeConstants.PROTEIN,
      ModelTypeConstants.RNA_SEQUENCE,
      ModelTypeConstants.SEQUENCE,
      ModelTypeConstants.DNA_OLIGO,
      ModelTypeConstants.RNA_OLIGO,
    ] as Array<string>
  ).includes(folderItemType);
}

export function includeColumnTypeOption(
  columnType: AutomationOutputFileColumnType,
  actionType: OutputFileAction,
  destinationInfo?: AutomationOutputFileDestinationInfo | null,
  entitySchema?: TagSchema | null,
  shouldCreateDataset: boolean = false,
  shouldPerformPlateTransfer: boolean = false
): boolean {
  const folderItemType = entitySchema?.folderItemType;
  if (SOURCE_COLUMN_TYPES.has(columnType)) {
    return isTransfer(actionType, shouldPerformPlateTransfer) || RESULTS_ACTION_TYPES.has(actionType);
  } else if (DESTINATION_COLUMN_TYPES.has(columnType)) {
    return includeDestinationColumnTypeOption(
      columnType,
      actionType,
      shouldPerformPlateTransfer,
      destinationInfo
    );
  } else if (ENTITY_COLUMN_TYPES.has(columnType)) {
    return includeEntityColumnTypeOption(columnType, actionType, shouldPerformPlateTransfer);
  } else if (AMOUNT_COLUMN_TYPES.has(columnType)) {
    // All the Amount columns are available for transfers only
    return isTransfer(actionType, shouldPerformPlateTransfer);
  } else if (RESIDUES_COLUMN_TYPES.has(columnType)) {
    return folderItemType ? isFolderItemTypeWithResidues(folderItemType) : false;
  } else if (CHEMICAL_STRUCTURE_COLUMN_TYPES.has(columnType)) {
    return folderItemType === ModelTypeConstants.MOLECULE;
  } else if (DATASET_COLUMN_TYPES.has(columnType)) {
    return shouldCreateDataset;
  } else if (PROCEDURE_CONDITION_REPLICATES_COLUMN_TYPES.has(columnType)) {
    return BenchlingConfig.PROCEDURES_ENABLE;
  } else {
    // Other column types are allowed for all output configs
    return true;
  }
}
function includeDestinationColumnTypeOption(
  columnType: AutomationOutputFileColumnType,
  actionType: OutputFileAction,
  shouldPerformPlateTransfer: boolean,
  destinationInfo?: AutomationOutputFileDestinationInfo | null
): boolean {
  // Output columns only make sense for transfers
  if (!isTransfer(actionType, shouldPerformPlateTransfer)) {
    return false;
  }
  if (destinationInfo && DESTINATION_COLUMN_TYPES.has(columnType)) {
    if (destinationInfo.boxSchema) {
      return (
        BOX_DESTINATION_COLUMN_TYPES.has(columnType) || CONTAINER_DESTINATION_COLUMN_TYPES.has(columnType)
      );
    }
    if (destinationInfo.containerSchema) {
      return CONTAINER_DESTINATION_COLUMN_TYPES.has(columnType);
    }
    if (destinationInfo.plateSchema) {
      return (
        PLATE_DESTINATION_COLUMN_TYPES.has(columnType) || CONTAINER_DESTINATION_COLUMN_TYPES.has(columnType)
      );
    }
  }
  // If this is a transfer with no destination schema specified, we could be using any of the columns.
  return true;
}

function includeEntityColumnTypeOption(
  columnType: AutomationOutputFileColumnType,
  actionType: OutputFileAction,
  shouldPerformPlateTransfer: boolean
): boolean {
  if (shouldPerformPlateTransfer && ENTITY_COLUMN_TYPES_FOR_PLATE_TRANSFER.has(columnType)) {
    return true;
  }
  if (actionType === OutputFileAction.PURE_RESULTS) {
    return ENTITY_COLUMN_TYPES_FOR_PURE_RESULTS.has(columnType);
  } else if (REGISTRATION_ACTION_TYPES.has(actionType)) {
    return ENTITY_COLUMN_TYPES_FOR_REGISTRATION.has(columnType);
  } else if (actionType === OutputFileAction.NO_ACTION && !shouldPerformPlateTransfer) {
    return false;
  } else {
    // shouldPerformPlateTransfer should route here and behave the same as a pure transfer, for now
    return ENTITY_COLUMN_TYPES_FOR_PURE_TRANSFER.has(columnType);
  }
}
